import type { DatabaseAttachment } from '../ai-chat-content/type';

function identityKey(docId: string, blockId: string): string | null {
  const normalizedDocId = docId.trim();
  const normalizedBlockId = blockId.trim();
  if (!normalizedDocId || !normalizedBlockId) {
    return null;
  }
  return JSON.stringify([normalizedDocId, normalizedBlockId]);
}

function normalizedSessionKey(sessionKey: string): string | null {
  const normalized = sessionKey.trim();
  return normalized || null;
}

export function hasDatabaseAttachmentIdentity(
  attachment: DatabaseAttachment,
  docId: string,
  blockId: string
): boolean {
  const expectedKey = identityKey(docId, blockId);
  const attachmentKey = identityKey(attachment.docId ?? '', attachment.blockId);
  return expectedKey !== null && attachmentKey === expectedKey;
}

export type FullscreenExistingContextState = 'exact' | 'mismatch' | 'none';

/**
 * Keeps fullscreen database selections across composer recreation without
 * allowing equal block IDs from different documents to share semantic scope.
 *
 * A single entry is sufficient because the UI has one active fullscreen
 * database. Replacing it deterministically evicts the previous selection,
 * bounding retained scope to at most one 500-row attachment.
 */
export class FullscreenDatabaseAttachmentCache {
  private entry:
    | {
        attachment: DatabaseAttachment;
        identity: string;
        sessionKey: string;
      }
    | undefined;

  get size(): number {
    return this.entry ? 1 : 0;
  }

  get(
    sessionKey: string,
    docId: string,
    blockId: string
  ): DatabaseAttachment | null {
    const session = this.activateSession(sessionKey);
    const identity = identityKey(docId, blockId);
    if (!session || !identity) {
      return null;
    }
    return this.entry?.identity === identity ? this.entry.attachment : null;
  }

  set(sessionKey: string, attachment: DatabaseAttachment): boolean {
    const session = this.activateSession(sessionKey);
    const identity = identityKey(attachment.docId ?? '', attachment.blockId);
    if (!session || !identity) {
      return false;
    }
    this.entry = { attachment, identity, sessionKey: session };
    return true;
  }

  delete(sessionKey: string, docId: string, blockId: string): void {
    const session = this.activateSession(sessionKey);
    const identity = identityKey(docId, blockId);
    if (
      session &&
      identity &&
      this.entry?.sessionKey === session &&
      this.entry.identity === identity
    ) {
      this.entry = undefined;
    }
  }

  clear(): void {
    this.entry = undefined;
  }

  private activateSession(sessionKey: string): string | null {
    const session = normalizedSessionKey(sessionKey);
    if (!session) {
      this.clear();
      return null;
    }
    if (this.entry && this.entry.sessionKey !== session) {
      this.clear();
    }
    return session;
  }
}

/**
 * Testable contract used by the composer for all tuple-sensitive paths.
 */
export class FullscreenDatabaseContextContract {
  constructor(private readonly cache: FullscreenDatabaseAttachmentCache) {}

  existingState(
    attachments: DatabaseAttachment[],
    docId: string,
    blockId: string
  ): FullscreenExistingContextState {
    const matches = attachments.filter(
      attachment =>
        attachment.source === 'fullscreen' && attachment.blockId === blockId
    );
    if (
      matches.some(attachment =>
        hasDatabaseAttachmentIdentity(attachment, docId, blockId)
      )
    ) {
      return 'exact';
    }
    return matches.length > 0 ? 'mismatch' : 'none';
  }

  restore(
    sessionKey: string,
    docId: string,
    blockId: string
  ): DatabaseAttachment | null {
    return this.cache.get(sessionKey, docId, blockId);
  }

  store(sessionKey: string, attachment: DatabaseAttachment): boolean {
    return this.cache.set(sessionKey, attachment);
  }

  clear(sessionKey: string, docId: string, blockId: string): void {
    this.cache.delete(sessionKey, docId, blockId);
  }

  acceptsEvent(currentDocId: string, eventDocId: string): boolean {
    const current = currentDocId.trim();
    const event = eventDocId.trim();
    return Boolean(current && event && current === event);
  }

  acceptsRowSelection(
    attachment: DatabaseAttachment | null,
    currentDocId: string,
    blockId: string
  ): boolean {
    return Boolean(
      attachment &&
      hasDatabaseAttachmentIdentity(attachment, currentDocId, blockId)
    );
  }
}
